<template>
  <div class="sector-listing">
    <template v-for="group in groups" :key="group.sector.id">
      <div
        v-if="group.split"
        class="sector-heading d-flex align-center ga-2 mt-3 mb-1 px-1"
        :class="{ 'text-disabled': group.soldOut }"
      >
        <span class="swatch rounded-sm" :style="{ background: headingColor(group) }" />
        <span class="text-caption font-weight-bold text-uppercase">{{ group.sector.name }}</span>
      </div>
      <button
        v-for="panel in group.panels"
        :key="panel.id"
        class="section-row d-flex justify-space-between align-center pa-3 mb-2 w-100"
        :class="{ active: panel.id === activeId }"
        :data-section="panel.section.id"
        :disabled="panel.soldOut"
        :style="{ borderLeft: `10px solid ${panelColor(panel)}` }"
        @click="emit('open', panel)"
      >
        <span class="font-weight-bold">{{ group.split ? panel.section.name : panel.sector.name }}</span>
        <span class="text-caption">{{ priceLabel(panel) }}</span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import formatCurrency from "@/filters/currency";
import type Sector from "@/types/Sector";
import type { SectionPanel, SectorGroup } from "@/utils/sectionSelection";

interface Props {
  groups: SectorGroup[];
  /** Fill of each map shape as drawn, by the id of the Sector or Section it is linked to. */
  colors: Record<string, string>;
  /** The row whose Section is open. */
  activeId: string | null;
}

const { groups, colors, activeId } = defineProps<Props>();

const emit = defineEmits<{
  open: [panel: SectionPanel];
}>();

defineOptions({
  name: "SectorListing",
});

/** Sold out, or without a colour of its own. Same grey as a disabled shape on the map. */
const GREY = "#bbbbbb";

function sectorColor(sector: Sector): string {
  return colors[sector.id] ?? sector.color ?? GREY;
}

/** A split Sector's own shape is usually just a group around its Sections' shapes, which carry the fill. */
function headingColor(group: SectorGroup): string {
  if (group.soldOut) return GREY;

  return colors[group.panels[0].id] ?? sectorColor(group.sector);
}

function panelColor(panel: SectionPanel): string {
  if (panel.soldOut) return GREY;

  return colors[panel.id] ?? sectorColor(panel.sector);
}

function priceLabel(panel: SectionPanel): string {
  if (panel.soldOut || panel.minPrice === null) return "Esgotado";

  return `A partir de ${formatCurrency(panel.minPrice)} + taxas`;
}
</script>

<style scoped>
.section-row {
  background: white;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  text-align: left;
}

.section-row.active {
  border-color: rgb(var(--v-theme-primary));
}

.section-row:disabled {
  opacity: 0.5;
}

.swatch {
  width: 14px;
  height: 14px;
}
</style>
